import pyodideManifest from "../../scripts/pyodide-assets-manifest.json";
import { configuredPythonRealmOrigin } from "./pythonRealmClient.js";

export const PYTHON_WHEEL_REFUSAL = Object.freeze({
    PACKAGE_NOT_ALLOWED: "wheel_package_not_allowed",
    MANIFEST_INVALID: "wheel_manifest_invalid",
    ASSET_UNAVAILABLE: "wheel_asset_unavailable",
    ASSET_VERIFICATION_FAILED: "wheel_asset_verification_failed",
    VERIFICATION_UNAVAILABLE: "wheel_verification_unavailable",
    CANCELLED: "wheel_request_cancelled",
});

const SHA256 = /^[a-f0-9]{64}$/;
const PACKAGE_NAME = /^[A-Za-z0-9](?:[A-Za-z0-9._-]{0,126}[A-Za-z0-9])?$/;
const SAFE_FILE_NAME = /^[A-Za-z0-9][A-Za-z0-9._+-]*$/;
const MAX_WHEELS = 8;
const MAX_TOTAL_WHEEL_BYTES = 64 * 1024 * 1024;

const REFUSAL_COPY = Object.freeze({
    [PYTHON_WHEEL_REFUSAL.PACKAGE_NOT_ALLOWED]:
        "That Python package and version are not in the approved package manifest.",
    [PYTHON_WHEEL_REFUSAL.MANIFEST_INVALID]:
        "The approved Python package manifest is invalid.",
    [PYTHON_WHEEL_REFUSAL.ASSET_UNAVAILABLE]:
        "An approved Python package could not be loaded.",
    [PYTHON_WHEEL_REFUSAL.ASSET_VERIFICATION_FAILED]:
        "An approved Python package failed integrity verification.",
    [PYTHON_WHEEL_REFUSAL.VERIFICATION_UNAVAILABLE]:
        "Python package integrity verification is unavailable.",
    [PYTHON_WHEEL_REFUSAL.CANCELLED]:
        "The run ended before the Python package was loaded.",
});

/**
 * Build the parent-side handler for a realm wheel request.
 *
 * `authorize` must perform the per-run consent decision. It is deliberately
 * called before the first fetch, while the checked manifest remains the only
 * source of package URLs and integrity metadata.
 */
export function createPythonWheelBroker({
    authorize,
    signal = null,
    fetchImpl = globalThis.fetch,
    cryptoImpl = globalThis.crypto,
    manifest = pyodideManifest,
    origin,
} = {}) {
    return async function brokerPythonWheel(request) {
        if (signal?.aborted) {
            return refused(PYTHON_WHEEL_REFUSAL.CANCELLED, request);
        }

        let plan;
        try {
            plan = resolveWheelPlan(manifest, request);
        } catch {
            return refused(PYTHON_WHEEL_REFUSAL.MANIFEST_INVALID, request);
        }
        if (!plan) {
            return refused(PYTHON_WHEEL_REFUSAL.PACKAGE_NOT_ALLOWED, request);
        }

        if (plan.authorizationRequired && typeof authorize !== "function") {
            return refused("consent_broker_unavailable", request);
        }

        if (plan.authorizationRequired) {
            let decision;
            try {
                decision = await authorize({
                    packageName: plan.packageName,
                    version: plan.version,
                    ...(nonEmpty(request?.reason)
                        ? { reason: nonEmpty(request.reason) }
                        : {}),
                });
            } catch {
                return refused("consent_broker_failed", request);
            }
            if (!decision?.ok || decision.approved !== true) {
                return jsonSafeDecision(decision)
                    || refused("consent_declined", request);
            }
        }

        if (signal?.aborted) {
            return refused(PYTHON_WHEEL_REFUSAL.CANCELLED, request);
        }
        // The worker sets this only after it has installed and re-verified the
        // exact manifest closure. Reuse still requires this run's consent, but
        // no package bytes need to be fetched or transferred again.
        if (request?.reuseInstalled === true) {
            return {
                ok: true,
                approved: true,
                packageName: plan.packageName,
                version: plan.version,
                reuseInstalled: true,
                wheels: [],
            };
        }
        if (typeof fetchImpl !== "function") {
            return refused(PYTHON_WHEEL_REFUSAL.ASSET_UNAVAILABLE, request);
        }
        if (typeof cryptoImpl?.subtle?.digest !== "function") {
            return refused(PYTHON_WHEEL_REFUSAL.VERIFICATION_UNAVAILABLE, request);
        }

        const wheels = [];
        let assetOrigin;
        try {
            assetOrigin = configuredPythonRealmOrigin(origin);
        } catch {
            return refused(PYTHON_WHEEL_REFUSAL.ASSET_UNAVAILABLE, request);
        }
        for (const wheel of plan.wheels) {
            if (signal?.aborted) {
                return refused(PYTHON_WHEEL_REFUSAL.CANCELLED, request);
            }

            let response;
            try {
                response = await fetchImpl(`${assetOrigin}${wheel.path}`, {
                    credentials: "omit",
                    cache: "force-cache",
                    mode: "cors",
                    redirect: "error",
                    ...(signal ? { signal } : {}),
                });
            } catch {
                return refused(
                    signal?.aborted
                        ? PYTHON_WHEEL_REFUSAL.CANCELLED
                        : PYTHON_WHEEL_REFUSAL.ASSET_UNAVAILABLE,
                    request,
                );
            }
            if (!response?.ok) {
                return refused(PYTHON_WHEEL_REFUSAL.ASSET_UNAVAILABLE, request);
            }

            const read = await readExactResponseBytes(response, wheel.size, signal);
            if (!read.ok) return refused(read.reason, request);
            const bytes = read.bytes;

            let digest;
            try {
                digest = await cryptoImpl.subtle.digest("SHA-256", bytes);
            } catch {
                return refused(PYTHON_WHEEL_REFUSAL.VERIFICATION_UNAVAILABLE, request);
            }
            if (hex(new Uint8Array(digest)) !== wheel.sha256) {
                return refused(
                    PYTHON_WHEEL_REFUSAL.ASSET_VERIFICATION_FAILED,
                    request,
                );
            }
            if (signal?.aborted) {
                return refused(PYTHON_WHEEL_REFUSAL.CANCELLED, request);
            }

            wheels.push({
                packageName: wheel.packageName,
                version: wheel.version,
                fileName: wheel.fileName,
                bytes,
            });
        }

        return {
            ok: true,
            approved: true,
            packageName: plan.packageName,
            version: plan.version,
            wheels,
        };
    };
}

export function resolveWheelPlan(manifest, request) {
    const packageName = nonEmpty(request?.packageName ?? request?.package ?? request?.name);
    const requestedVersion = nonEmpty(request?.version);
    if (!PACKAGE_NAME.test(packageName)) return null;

    const index = validateManifest(manifest);
    if (!index.roots.has(packageName)) return null;

    const root = index.packages.get(packageName);
    if (!root || (requestedVersion && requestedVersion !== root.version)) return null;

    const ordered = [];
    const visited = new Set();
    const visiting = new Set();

    function visit(name) {
        if (visited.has(name)) return;
        if (visiting.has(name)) throw new Error("wheel dependency cycle");
        const record = index.packages.get(name);
        if (!record) throw new Error("wheel dependency is missing");

        visiting.add(name);
        for (const dependency of record.depends) visit(dependency);
        visiting.delete(name);
        visited.add(name);

        const asset = index.assets.get(record.fileName);
        if (!asset) throw new Error("wheel asset is missing");
        ordered.push({
            packageName: record.name,
            version: record.version,
            fileName: record.fileName,
            path: assetPath(manifest.outputSubdirectory, record.fileName),
            size: asset.size,
            sha256: asset.sha256,
        });
    }

    visit(packageName);
    const expectedMissing = request?.missingPackages;
    if (
        expectedMissing !== undefined
        && (
            !Array.isArray(expectedMissing)
            || expectedMissing.length > MAX_WHEELS
            || new Set(expectedMissing).size !== expectedMissing.length
            || expectedMissing.some((name) => !PACKAGE_NAME.test(name))
        )
    ) {
        throw new Error("wheel request has invalid missing-package metadata");
    }
    const wheels = expectedMissing === undefined
        ? ordered
        : ordered.filter((wheel) => expectedMissing.includes(wheel.packageName));
    if (
        expectedMissing !== undefined
        && (
            expectedMissing.length !== wheels.length
            || expectedMissing.some((name, index) => name !== wheels[index]?.packageName)
        )
    ) {
        throw new Error("wheel request does not match the pinned missing closure");
    }
    const totalSize = wheels.reduce((total, wheel) => total + wheel.size, 0);
    if (
        wheels.length > MAX_WHEELS
        || (wheels.length === 0 && request?.reuseInstalled !== true)
        || !Number.isSafeInteger(totalSize)
        || totalSize > MAX_TOTAL_WHEEL_BYTES
    ) {
        throw new Error("wheel delivery exceeds transport bounds");
    }
    return {
        packageName,
        version: root.version,
        authorizationRequired: index.onDemandRoots.has(packageName),
        wheels,
    };
}

function validateManifest(manifest) {
    if (!manifest || typeof manifest !== "object" || Array.isArray(manifest)) {
        throw new Error("manifest must be an object");
    }
    assetPath(manifest.outputSubdirectory, "probe.whl");

    const curated = manifest.curatedPackages;
    const curatedRecords = curated?.packages;
    const onDemand = manifest.onDemandPackages;
    if (
        !curated
        || !Array.isArray(curated.roots)
        || curated.roots.length === 0
        || !Array.isArray(curatedRecords)
        || !onDemand
        || !Array.isArray(onDemand.roots)
        || onDemand.roots.length === 0
        || !Array.isArray(onDemand.packages)
        || onDemand.packages.length === 0
        || !Array.isArray(manifest.assets)
    ) {
        throw new Error("manifest package lists are invalid");
    }

    const curatedNames = new Set();
    const packages = new Map();
    for (const record of curatedRecords) {
        validatePackageRecord(record);
        if (curatedNames.has(record.name)) throw new Error("duplicate curated package");
        curatedNames.add(record.name);
        packages.set(record.name, record);
    }

    for (const record of onDemand.packages) {
        validatePackageRecord(record);
        if (packages.has(record.name)) {
            throw new Error("duplicate on-demand package");
        }
        packages.set(record.name, record);
    }

    const roots = new Set();
    const curatedRoots = new Set();
    for (const root of curated.roots) {
        if (
            !PACKAGE_NAME.test(root)
            || roots.has(root)
            || !curatedNames.has(root)
        ) {
            throw new Error("invalid curated root");
        }
        roots.add(root);
        curatedRoots.add(root);
    }
    const onDemandRoots = new Set();
    for (const root of onDemand.roots) {
        if (!PACKAGE_NAME.test(root) || roots.has(root) || !packages.has(root)) {
            throw new Error("invalid on-demand root");
        }
        roots.add(root);
        onDemandRoots.add(root);
    }

    const assets = new Map();
    for (const asset of manifest.assets) {
        if (!["package", "on-demand-package"].includes(asset?.kind)) continue;
        if (
            !SAFE_FILE_NAME.test(asset.fileName)
            || !Number.isSafeInteger(asset.size)
            || asset.size <= 0
            || asset.size > MAX_TOTAL_WHEEL_BYTES
            || !SHA256.test(asset.sha256)
            || assets.has(asset.fileName)
        ) {
            throw new Error("invalid wheel asset");
        }
        assets.set(asset.fileName, asset);
    }

    if (assets.size !== packages.size) {
        throw new Error("packages and assets differ");
    }
    for (const record of packages.values()) {
        if (!assets.has(record.fileName)) throw new Error("package asset missing");
        const expectedKind = curatedNames.has(record.name)
            ? "package"
            : "on-demand-package";
        if (assets.get(record.fileName).kind !== expectedKind) {
            throw new Error("package asset kind mismatch");
        }
        for (const dependency of record.depends) {
            if (!packages.has(dependency)) {
                throw new Error("package dependency missing");
            }
        }
    }
    for (const fileName of assets.keys()) {
        if (![...packages.values()].some((record) => record.fileName === fileName)) {
            throw new Error("unreferenced package asset");
        }
    }

    return {
        roots,
        curatedNames,
        curatedRoots,
        onDemandRoots,
        packages,
        assets,
    };
}

function validatePackageRecord(record) {
    if (
        !record
        || !PACKAGE_NAME.test(record.name)
        || !nonEmpty(record.version)
        || !SAFE_FILE_NAME.test(record.fileName)
        || !Array.isArray(record.depends)
        || new Set(record.depends).size !== record.depends.length
        || record.depends.some((name) => !PACKAGE_NAME.test(name))
    ) {
        throw new Error("invalid package record");
    }
}

async function readExactResponseBytes(response, expectedSize, signal) {
    const contentLength = response.headers?.get?.("content-length");
    if (contentLength != null) {
        const normalized = String(contentLength).trim();
        if (
            !/^(?:0|[1-9]\d*)$/.test(normalized)
            || Number(normalized) !== expectedSize
        ) {
            return {
                ok: false,
                reason: PYTHON_WHEEL_REFUSAL.ASSET_VERIFICATION_FAILED,
            };
        }
    }

    const reader = response.body?.getReader?.();
    if (!reader || typeof reader.read !== "function") {
        return {
            ok: false,
            reason: PYTHON_WHEEL_REFUSAL.ASSET_UNAVAILABLE,
        };
    }

    let bytes;
    try {
        bytes = new Uint8Array(expectedSize);
    } catch {
        try {
            await reader.cancel?.();
        } catch { /* best effort */ }
        return {
            ok: false,
            reason: PYTHON_WHEEL_REFUSAL.ASSET_VERIFICATION_FAILED,
        };
    }

    let offset = 0;
    try {
        while (true) {
            if (signal?.aborted) {
                await reader.cancel?.();
                return {
                    ok: false,
                    reason: PYTHON_WHEEL_REFUSAL.CANCELLED,
                };
            }
            const chunk = await reader.read();
            if (chunk?.done) break;
            if (!(chunk?.value instanceof Uint8Array)) {
                await reader.cancel?.();
                return {
                    ok: false,
                    reason: PYTHON_WHEEL_REFUSAL.ASSET_VERIFICATION_FAILED,
                };
            }
            if (offset + chunk.value.byteLength > expectedSize) {
                await reader.cancel?.();
                return {
                    ok: false,
                    reason: PYTHON_WHEEL_REFUSAL.ASSET_VERIFICATION_FAILED,
                };
            }
            bytes.set(chunk.value, offset);
            offset += chunk.value.byteLength;
        }
    } catch {
        return {
            ok: false,
            reason: signal?.aborted
                ? PYTHON_WHEEL_REFUSAL.CANCELLED
                : PYTHON_WHEEL_REFUSAL.ASSET_UNAVAILABLE,
        };
    } finally {
        try {
            reader.releaseLock?.();
        } catch { /* best effort */ }
    }

    if (offset !== expectedSize) {
        return {
            ok: false,
            reason: PYTHON_WHEEL_REFUSAL.ASSET_VERIFICATION_FAILED,
        };
    }
    return { ok: true, bytes: bytes.buffer };
}

function assetPath(outputSubdirectory, fileName) {
    const directory = nonEmpty(outputSubdirectory)
        .replaceAll("\\", "/")
        .replace(/^\/+|\/+$/g, "");
    const segments = directory.split("/");
    if (
        !directory
        || segments.some((segment) => !segment || segment === "." || segment === "..")
        || !SAFE_FILE_NAME.test(fileName)
    ) {
        throw new Error("invalid wheel asset path");
    }
    return `/${directory}/${encodeURIComponent(fileName)}`;
}

function jsonSafeDecision(decision) {
    if (!decision || typeof decision !== "object") return null;
    const reason = nonEmpty(decision.refusal?.reason);
    const message = nonEmpty(decision.refusal?.message)
        || nonEmpty(decision.error?.message)
        || nonEmpty(decision.error)
        || "The Python package request was refused.";
    return {
        ok: false,
        approved: false,
        outcome: "refused",
        refusal: {
            reason: reason || "consent_declined",
            message,
            ...(decision.refusal?.capability
                ? { capability: decision.refusal.capability }
                : {}),
            ...(decision.refusal?.trigger ? { trigger: decision.refusal.trigger } : {}),
        },
        error: message,
    };
}

function refused(reason, request) {
    const message = REFUSAL_COPY[reason] || "The Python package request was refused.";
    return {
        ok: false,
        approved: false,
        outcome: "refused",
        refusal: {
            reason,
            message,
            ...(request
                ? {
                    capability: {
                        kind: "wheel",
                        packageName: nonEmpty(
                            request.packageName ?? request.package ?? request.name,
                        ),
                        ...(nonEmpty(request.version)
                            ? { version: nonEmpty(request.version) }
                            : {}),
                        ...(nonEmpty(request.reason)
                            ? { reason: nonEmpty(request.reason) }
                            : {}),
                    },
                }
                : {}),
        },
        error: message,
    };
}

function nonEmpty(value) {
    if (value == null) return "";
    return String(value).trim();
}

function hex(bytes) {
    return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
}
